import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefData } from "@/context/RefDataContext";

/**
 * Lightweight modal for creating a single related entity from a parent page.
 *
 * Props:
 *  - open, onOpenChange
 *  - endpoint            e.g. "/tasks"
 *  - title               e.g. "New Task"
 *  - fields              [{ key, label, type, optionsRef|options, required }]
 *  - initialForm         object - defaults for fields (including fixed parent ids)
 *  - lockedKeys          array of field keys that should be displayed but disabled
 *  - onCreated(record)   callback after successful create
 */
export default function AddInlineDialog({
  open,
  onOpenChange,
  endpoint,
  title,
  fields,
  initialForm,
  lockedKeys = [],
  onCreated,
}) {
  const ref = useRefData();
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initialForm);
  }, [open, initialForm]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      const { data } = await api.post(endpoint, payload);
      toast.success("Created");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border border-white/10 text-zinc-100 max-w-lg"
        data-testid="add-inline-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white" style={{ fontFamily: "Outfit" }}>
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          {fields.map((f) => {
            const locked = lockedKeys.includes(f.key);
            const tid = `add-inline-field-${f.key}`;
            const wrapperCls = f.full ? "col-span-2" : "col-span-2 md:col-span-1";

            if (f.type === "textarea") {
              return (
                <div key={f.key} className={wrapperCls}>
                  <Label className="text-xs text-zinc-400">{f.label}</Label>
                  <Textarea
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[70px]"
                    data-testid={tid}
                    disabled={locked}
                  />
                </div>
              );
            }
            if (f.type === "select") {
              let opts = f.optionsRef ? ref[f.optionsRef] || [] : f.options || [];
              if (f.filterBy) {
                const filterValue = form[f.filterBy];
                if (!filterValue) {
                  opts = [];
                } else {
                  opts = opts.filter((o) =>
                    typeof o === "string" ? true : o[f.filterBy] === filterValue
                  );
                }
              }
              const filterLabel = f.filterBy === "project_id" ? "CDARK" : (f.filterBy || "").replace("_", " ");
              const placeholder =
                f.filterBy && !form[f.filterBy]
                  ? `Select ${filterLabel} first`
                  : "Select…";
              return (
                <div key={f.key} className={wrapperCls}>
                  <Label className="text-xs text-zinc-400">{f.label}</Label>
                  <Select
                    value={form[f.key] || ""}
                    onValueChange={(v) => setForm({ ...form, [f.key]: v })}
                    disabled={locked}
                  >
                    <SelectTrigger
                      className="mt-1.5 bg-zinc-900 border-white/10 text-white disabled:opacity-70"
                      data-testid={tid}
                    >
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100 max-h-72">
                      {opts.length === 0 ? (
                        <div className="px-2 py-3 text-xs text-zinc-500">
                          {f.filterBy && !form[f.filterBy]
                            ? `Pick a ${filterLabel} first`
                            : "No options"}
                        </div>
                      ) : (
                        opts.map((o) => {
                          const v = typeof o === "string" ? o : o.value;
                          const lbl = typeof o === "string" ? o : o.label;
                          return (
                            <SelectItem key={v} value={v}>
                              {lbl}
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            return (
              <div key={f.key} className={wrapperCls}>
                <Label className="text-xs text-zinc-400">{f.label}</Label>
                <Input
                  type={f.type || "text"}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5 bg-zinc-900 border-white/10 text-white disabled:opacity-70"
                  placeholder={f.placeholder}
                  disabled={locked}
                  data-testid={tid}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-300 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid="add-inline-save-btn"
          >
            {saving ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
